import styled from 'styled-components';
import { theme } from 'antd';
import type { GlobalToken } from 'antd';
import { ReactNode } from 'react';

export type LabelSize = 's' | 'm' | 'l';

export interface LabelProps {
  /** Label text */
  children: ReactNode;
  /** Label size */
  size?: LabelSize;
  /** Whether the field is required */
  required?: boolean;
  /** HTML for attribute to associate with input */
  htmlFor?: string;
  /** Additional className */
  className?: string;
  /** Tooltip/help icon */
  tooltip?: ReactNode;
  /** Additional style */
  style?: React.CSSProperties;
}

const getSizeConfig = (token: GlobalToken, size: LabelSize) => {
  const sizeMap = {
    s: {
      fontSize: token.fontSizeSM - 2,
      lineHeight: '16px',
    },
    m: {
      fontSize: token.fontSizeSM,
      lineHeight: '16px',
    },
    l: {
      fontSize: token.fontSize,
      lineHeight: '18px',
    },
  };
  return sizeMap[size];
};

interface StyledProps {
  $token: GlobalToken;
  $size: LabelSize;
}

const StyledLabel = styled.label<StyledProps>`
  display: inline-flex;
  align-items: center;
  gap: ${({ $token }) => $token.marginXXS}px;
  font-family: ${({ $token }) => $token.fontFamily};
  font-size: ${({ $token, $size }) => getSizeConfig($token, $size).fontSize}px;
  line-height: ${({ $token, $size }) =>
    getSizeConfig($token, $size).lineHeight};
  font-weight: 400;
  color: ${({ $token }) => $token.colorText};
`;

const RequiredMark = styled.span<{ $token: GlobalToken }>`
  color: ${({ $token }) => $token.colorError};
  font-weight: ${({ $token }) => $token.fontWeightStrong};
`;

const TooltipWrapper = styled.span<{ $token: GlobalToken }>`
  display: inline-flex;
  align-items: center;
  color: ${({ $token }) => $token.colorPrimary};
  cursor: help;

  svg {
    width: 14px;
    height: 14px;
  }
`;

/**
 * Label component based on Figma Design System
 *
 * A form label component with optional required indicator and tooltip.
 * Now uses Ant Design theme tokens for consistent styling.
 *
 * @example
 * ```tsx
 * import { Label } from '@libertify/ui';
 *
 * <Label>Email Address</Label>
 * <Label required>Password</Label>
 * <Label size="l" required tooltip={<QuestionIcon />}>Username</Label>
 * ```
 */
export const Label = ({
  children,
  size = 'm',
  required,
  htmlFor,
  className,
  tooltip,
  style,
}: LabelProps) => {
  const { token } = theme.useToken();

  return (
    <StyledLabel
      $token={token}
      $size={size}
      htmlFor={htmlFor}
      className={className}
      style={style}
    >
      {children}
      {required && <RequiredMark $token={token}>*</RequiredMark>}
      {tooltip && <TooltipWrapper $token={token}>{tooltip}</TooltipWrapper>}
    </StyledLabel>
  );
};

export default Label;
